/**
 * APX WorkCenter — Admin Settings
 * Google Calendar connection management, morning briefing controls, and workflow status.
 * Accessible only to Admin (Mr. B).
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { trpc, trpcClient } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { isSuperAdmin, isManager } from "@/lib/roleGuard";
import DashboardLayout from "@/components/DashboardLayout";

// Icons
function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function UnlinkIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-3.782L3.56 7.566a4.5 4.5 0 016.364-6.364l3.062 3.062m-3.367 3.367l-3.317 3.317m7.783 7.783L19.44 16.434a4.5 4.5 0 00-6.364-6.364l-3.062 3.062m-3.367 3.367l-3.317 3.317" />
    </svg>
  );
}

function BriefingIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
    </svg>
  );
}

// Xero icon
function XeroIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1.5 14.5l-3.5-3.5 1.06-1.06 2.44 2.44 4.94-4.94 1.06 1.06-6 6z" />
    </svg>
  );
}

const FAVICON_SETTINGS_QUERY = { keys: ["site_favicon_url"] };

function FaviconSettings() {
  const inputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();
  const faviconQuery = trpc.appSettings.getMany.useQuery(FAVICON_SETTINGS_QUERY, { staleTime: 60_000 });
  const [isUploading, setIsUploading] = useState(false);

  const updateFavicon = async (file?: File) => {
    if (!file) return;
    const validTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon"];
    if (!validTypes.includes(file.type)) return toast.error("Use a PNG or ICO favicon file.");
    if (file.size > 512 * 1024) return toast.error("Favicon must be 512 KB or smaller.");
    setIsUploading(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Could not read favicon file"));
        reader.readAsDataURL(file);
      });
      const result = await trpcClient.appSettings.uploadFavicon.mutate({
        fileName: file.name,
        mimeType: file.type as "image/png" | "image/x-icon" | "image/vnd.microsoft.icon",
        fileDataBase64: dataUrl.split(",")[1] ?? "",
      });
      document.querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="shortcut icon"]').forEach((link) => { link.href = result.url; });
      window.dispatchEvent(new CustomEvent("apx:favicon-change", { detail: result.url }));
      await utils.appSettings.getMany.invalidate(FAVICON_SETTINGS_QUERY);
      toast.success("Favicon updated", { description: "The new icon is active. Reopen existing tabs to refresh their cached icon." });
    } catch (error) {
      toast.error("Could not update favicon", { description: error instanceof Error ? error.message : "Please try again." });
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div style={{ background: "oklch(0.93 0.008 240)", border: "1px solid oklch(0 0 0 / 10%)", borderRadius: "1rem", padding: "1.5rem", marginBottom: "1.5rem" }}>
      <div style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)", fontSize: "1rem", fontWeight: 700, marginBottom: "0.5rem" }}>Site Favicon</div>
      <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>Upload the square icon shown in browser tabs and bookmarks. Use a PNG or ICO file, 512 KB or smaller.</p>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-lg border flex items-center justify-center overflow-hidden" style={{ background: "oklch(0.97 0.004 240)", borderColor: "oklch(0 0 0 / 12%)" }}>
          {faviconQuery.data?.site_favicon_url ? <img src={faviconQuery.data.site_favicon_url} alt="Current site favicon" className="w-8 h-8 object-contain" /> : <span className="text-xs">APX</span>}
        </div>
        <button type="button" onClick={() => inputRef.current?.click()} disabled={isUploading} className="rounded-md px-4 py-2 text-sm font-semibold" style={{ background: "oklch(0.72 0.14 75)", color: "oklch(0.12 0.04 240)" }}>
          {isUploading ? "Uploading…" : "Upload New Favicon"}
        </button>
        <input ref={inputRef} type="file" accept=".png,.ico,image/png,image/x-icon,image/vnd.microsoft.icon" className="hidden" onChange={(event) => updateFavicon(event.target.files?.[0])} />
      </div>
    </div>
  );
}


// // ─── Xero Integration Card ────────────────────────────────────────────
function XeroCard() {
  const { user } = useAuth();
  const xeroStatus = trpc.xero.status.useQuery();
  const xeroAuthUrl = trpc.xero.getAuthUrl.useQuery();
  const disconnectMutation = trpc.xero.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Xero disconnected.");
      xeroStatus.refetch();
    },
    onError: (err) => toast.error("Failed to disconnect Xero", { description: err.message }),
  });

  const s = {
    card: {
      background: "oklch(0.93 0.008 240)",
      border: "1px solid oklch(0 0 0 / 10%)",
      borderRadius: "1rem",
      padding: "1.5rem",
      marginBottom: "1.5rem",
    },
    sectionTitle: {
      fontFamily: "'Playfair Display', serif",
      color: "oklch(0.08 0.01 240)",
      fontSize: "1rem",
      fontWeight: 700,
      marginBottom: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    btn: (variant: "gold" | "outline" | "danger") => ({
      padding: "0.5rem 1rem",
      borderRadius: "0.5rem",
      fontSize: "0.9rem",
      fontWeight: 600,
      cursor: "pointer",
      border: "1px solid",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      transition: "opacity 0.15s",
      ...(variant === "gold"
        ? { background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))", color: "oklch(0.12 0.04 240)", borderColor: "transparent" }
        : variant === "danger"
        ? { background: "oklch(0.15 0.01 240 / 20%)", color: "oklch(0.08 0.01 240)", borderColor: "oklch(0.15 0.01 240 / 40%)" }
        : { background: "oklch(0.95 0.006 240)", color: "oklch(0.14 0.04 240)", borderColor: "oklch(1 0 0 / 10%)" }),
    }),
    badge: (connected: boolean) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      padding: "0.2rem 0.6rem",
      borderRadius: "9999px",
      fontSize: "0.875rem",
      fontWeight: 600,
      background: connected ? "oklch(0.92 0.005 240 / 15%)" : "oklch(0.5 0.02 240 / 15%)",
      color: connected ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
      border: `1px solid ${connected ? "oklch(0.92 0.005 240 / 30%)" : "oklch(0.5 0.02 240 / 20%)"}`,
    }),
  };

  const isConnected = xeroStatus.data?.connected ?? false;
  const configured = xeroAuthUrl.data?.configured ?? false;

  const handleReconnect = () => {
    if (!configured) {
      toast.error("Xero credentials not configured", {
        description: "XERO_CLIENT_ID and XERO_CLIENT_SECRET must be set in the hub secrets.",
      });
      return;
    }
    const url = xeroAuthUrl.data?.url;
    if (url) window.location.href = url;
    else toast.error("Could not generate Xero auth URL.");
  };

  return (
    <div style={s.card}>
      <div style={s.sectionTitle}>
        <XeroIcon />
        Xero — Accounting Integration
      </div>
      <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
        Connect APX's Xero account to enable live P&amp;L data, client financial summaries, and
        automated bookkeeping workflows. One connection covers all client organisations.
      </p>

      <div
        className="flex items-center justify-between rounded-lg px-4 py-3 mb-4"
        style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(1 0 0 / 6%)" }}
      >
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span style={{ color: "oklch(0.12 0.04 240)", fontSize: "0.875rem", fontWeight: 600 }}>
              APX
            </span>
            <span style={s.badge(isConnected)}>
              {isConnected ? <><CheckIcon /> Connected</> : "Not connected"}
            </span>
          </div>
          {isConnected && xeroStatus.data?.tenantName && (
            <div style={{ color: "oklch(0.28 0.03 240)", fontSize: "0.875rem" }}>
              Tenant: {xeroStatus.data.tenantName}
            </div>
          )}
          {isConnected && xeroStatus.data?.updatedAt && (
            <div style={{ color: "oklch(0.28 0.03 240)", fontSize: "0.875rem", marginTop: "0.1rem" }}>
              Last connected: {new Date(xeroStatus.data.updatedAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {isConnected ? (
            <>
              <button
                style={s.btn("gold")}
                onClick={handleReconnect}
                disabled={!configured}
              >
                <LinkIcon />
                Reconnect Xero
              </button>
              <button
                style={s.btn("danger")}
                onClick={() => disconnectMutation.mutate({ callerEmail: user?.email ?? "" })}
                disabled={disconnectMutation.isPending}
              >
                <UnlinkIcon />
                Disconnect
              </button>
            </>
          ) : (
            <button
              style={s.btn("gold")}
              onClick={handleReconnect}
              disabled={!configured}
            >
              <LinkIcon />
              Connect Xero
            </button>
          )}
        </div>
      </div>

      {!configured && (
        <div
          className="rounded-lg p-3 text-sm"
          style={{ background: "oklch(0.92 0.005 240 / 10%)", border: "1px solid oklch(0.92 0.005 240 / 25%)", color: "oklch(0.08 0.01 240)" }}
        >
          <strong>Setup required:</strong> Add <code>XERO_CLIENT_ID</code> and <code>XERO_CLIENT_SECRET</code> to the hub secrets to enable Xero integration.
        </div>
      )}
    </div>
  );
}

// ─── Standard Connection Protocol Card ───────────────────────────────────────────────
function ConnectionSettingsCard() {
  const { user } = useAuth();
  const { data: syncStatus, refetch: refetchStatus } = trpc.email.syncStatus.useQuery(
    { callerEmail: user?.email ?? "" },
    { refetchInterval: 30000 }
  );

  const syncMutation = trpc.email.sync.useMutation({
    onSuccess: (result) => {
      toast.success(`Sync complete — ${result.synced} new emails stored`);
      refetchStatus();
    },
    onError: (err) => toast.error("Sync failed", { description: err.message }),
  });

  const s = {
    card: {
      background: "oklch(0.95 0.006 240)",
      border: "1px solid oklch(0 0 0 / 12%)",
      borderRadius: "0.875rem",
      padding: "1.5rem",
      marginBottom: "1.25rem",
    } as React.CSSProperties,
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      fontSize: "0.9rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
      color: "oklch(0.08 0.01 240)",
      marginBottom: "1rem",
      fontFamily: "'Playfair Display', serif",
    } as React.CSSProperties,
    connRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.75rem 1rem",
      borderRadius: "0.5rem",
      background: "oklch(0.94 0.018 220)",
      border: "1px solid oklch(0 0 0 / 10%)",
      marginBottom: "0.625rem",
    } as React.CSSProperties,
  };

  const gmailConnected = true; // Always connected — apxadvisor@gmail.com via App Password
  const driveConnected = true; // Always connected — same Google OAuth token

  const lastSyncTime = syncStatus?.lastSyncedAt
    ? new Date(syncStatus.lastSyncedAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "Never synced";

  return (
    <div style={s.card}>
      <div style={s.sectionTitle}>
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
        </svg>
        Standard Connection Protocol
      </div>

      <p className="text-sm mb-5" style={{ color: "oklch(0.14 0.04 240)" }}>
        These are the firm's locked data connections. Both are authenticated under
        <strong style={{ color: "oklch(0.08 0.01 240)" }}> apxadvisor@gmail.com</strong> and are
        protected — they cannot be changed without Director approval.
      </p>

      {/* Gmail Connection */}
      <div style={s.connRow}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" style={{ color: "oklch(0.08 0.01 240)" }}>
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: "oklch(0.12 0.04 240)" }}>Gmail Inbox</p>
            <p className="text-sm" style={{ color: "oklch(0.28 0.03 240)" }}>apxadvisor@gmail.com · IMAP via App Password · DB-cached</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm" style={{ color: "oklch(0.28 0.03 240)" }}>
              {syncStatus?.totalEmails ?? 0} emails stored
            </p>
            <p className="text-sm" style={{ color: "oklch(0.22 0.03 240)" }}>Last sync: {lastSyncTime}</p>
          </div>
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold"
            style={{ background: "oklch(0.92 0.005 240 / 12%)", color: "oklch(0.08 0.01 240)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}>
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3"/></svg>
            Active
          </span>
        </div>
      </div>

      {/* Google Drive Connection */}
      <div style={s.connRow}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" style={{ color: "oklch(0.08 0.01 240)" }}>
              <path d="M7.71 3.5L1.15 15l3.43 5.5h15.24L21.85 15 15.29 3.5H7.71zm.57 1h6.44l5.7 9.5H2.58l5.7-9.5zM4.58 16h14.84l-2.57 4H7.15l-2.57-4z"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: "oklch(0.12 0.04 240)" }}>Google Drive</p>
            <p className="text-sm" style={{ color: "oklch(0.28 0.03 240)" }}>APX Accounting Info / Accounting Files · OAuth2 · A–Z indexed</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm" style={{ color: "oklch(0.28 0.03 240)" }}>26 letter folders</p>
            <p className="text-sm" style={{ color: "oklch(0.22 0.03 240)" }}>Instant dropdown — no index load</p>
          </div>
          <span className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold"
            style={{ background: "oklch(0.92 0.005 240 / 12%)", color: "oklch(0.08 0.01 240)", border: "1px solid oklch(0.92 0.005 240 / 25%)" }}>
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3"/></svg>
            Active
          </span>
        </div>
      </div>

      {/* Sync Controls */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={() => syncMutation.mutate({ callerEmail: user?.email ?? "" })}
          disabled={syncMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
          style={{
            background: syncMutation.isPending ? "oklch(0.97 0.005 85)" : "oklch(0.92 0.005 240 / 15%)",
            border: "1px solid oklch(0.92 0.005 240 / 40%)",
            color: syncMutation.isPending ? "oklch(0.35 0.02 240)" : "oklch(0.08 0.01 240)",
          }}
        >
          <svg className={`w-4 h-4 ${syncMutation.isPending ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {syncMutation.isPending ? "Syncing Gmail…" : "Sync Gmail Now"}
        </button>
        <span className="text-sm" style={{ color: "oklch(0.25 0.03 240)" }}>
          Auto-syncs every 15 min · incremental (new emails only)
        </span>
      </div>

      {/* Lock notice */}
      <div className="mt-4 rounded-lg px-4 py-3 text-sm"
        style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "1px solid oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}>
        <strong style={{ color: "oklch(0.08 0.01 240)" }}>🔒 Protected Protocol:</strong> These connections are the firm's standard data pipeline.
        Gmail uses a permanent App Password (no expiry). Drive uses OAuth2 with auto-refresh.
        Both are locked to <strong style={{ color: "oklch(0.08 0.01 240)" }}>apxadvisor@gmail.com</strong> and
        require Director approval to modify.
      </div>
    </div>
  );
}

// ─── Webhook Diagnostics Card ───────────────────────────────────────────────
function WebhookDiagnosticsCard() {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const { data: logs, isLoading, refetch, isFetching } = trpc.zapier.webhookLog.useQuery(
    { limit: 20 },
    { refetchInterval: 30000 }
  );

  const s = {
    card: {
      background: "oklch(0.95 0.006 240)",
      border: "1px solid oklch(0 0 0 / 12%)",
      borderRadius: "0.875rem",
      padding: "1.5rem",
      marginBottom: "1.25rem",
    } as React.CSSProperties,
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      fontSize: "0.9rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
      color: "oklch(0.08 0.01 240)",
      marginBottom: "1rem",
      fontFamily: "'Playfair Display', serif",
    } as React.CSSProperties,
  };

  const statusColor = (status: string) => {
    if (status === "processed") return { bg: "oklch(0.88 0.12 145 / 20%)", text: "oklch(0.28 0.12 145)", border: "oklch(0.88 0.12 145 / 40%)" };
    if (status === "error") return { bg: "oklch(0.88 0.12 25 / 20%)", text: "oklch(0.35 0.12 25)", border: "oklch(0.88 0.12 25 / 40%)" };
    return { bg: "oklch(0.88 0.08 240 / 20%)", text: "oklch(0.28 0.08 240)", border: "oklch(0.88 0.08 240 / 40%)" };
  };

  const formatPayload = (raw: string | null) => {
    if (!raw) return "(empty)";
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };

  return (
    <div style={s.card}>
      <div style={{ ...s.sectionTitle, justifyContent: "space-between" }}>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
          </svg>
          Webhook Diagnostics
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-medium transition-all"
          style={{
            background: "oklch(0.92 0.005 240 / 15%)",
            border: "1px solid oklch(0.92 0.005 240 / 40%)",
            color: "oklch(0.08 0.01 240)",
            cursor: isFetching ? "not-allowed" : "pointer",
          }}
        >
          <svg className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
        Last 20 incoming Zapier webhook hits — auto-refreshes every 30 seconds. Use this to diagnose Zap field mapping issues.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.35 0.02 240)" }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        </div>
      ) : !logs || logs.length === 0 ? (
        <div className="text-center py-8 text-sm" style={{ color: "oklch(0.35 0.02 240)" }}>
          No webhook hits recorded yet.
        </div>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => {
            const sc = statusColor(log.status);
            const isExpanded = expandedId === log.id;
            const ts = log.createdAt ? new Date(log.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
            const payload = formatPayload(log.payload);
            const hasBody = payload !== "(empty)" && payload.includes('message_body') && !payload.includes('"message_body": ""') && !payload.includes("'message_body': ''");

            return (
              <div
                key={log.id}
                className="rounded-lg overflow-hidden"
                style={{ border: `1px solid ${sc.border}`, background: sc.bg }}
              >
                {/* Row header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : log.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                  style={{ cursor: "pointer", background: "transparent" }}
                >
                  {/* Status badge */}
                  <span
                    className="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
                    style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
                  >
                    {log.status}
                  </span>

                  {/* Zap type */}
                  <span className="flex-shrink-0 text-xs font-mono px-2 py-0.5 rounded" style={{ background: "oklch(0.92 0.005 240 / 30%)", color: "oklch(0.12 0.04 240)" }}>
                    {log.zapType ?? "unknown"}
                  </span>

                  {/* Timestamp */}
                  <span className="text-xs" style={{ color: "oklch(0.28 0.03 240)" }}>{ts}</span>

                  {/* Body indicator */}
                  {hasBody ? (
                    <span className="ml-auto flex-shrink-0 text-xs font-medium" style={{ color: "oklch(0.28 0.12 145)" }}>✓ body</span>
                  ) : (
                    <span className="ml-auto flex-shrink-0 text-xs font-medium" style={{ color: "oklch(0.35 0.12 25)" }}>⚠ no body</span>
                  )}

                  {/* Error snippet */}
                  {log.errorMessage && (
                    <span className="text-xs truncate max-w-xs" style={{ color: "oklch(0.35 0.12 25)" }}>{log.errorMessage}</span>
                  )}

                  {/* Expand chevron */}
                  <svg
                    className={`w-4 h-4 flex-shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    style={{ color: "oklch(0.35 0.02 240)" }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                  </svg>
                </button>

                {/* Expanded payload */}
                {isExpanded && (
                  <div className="px-4 pb-4">
                    {log.errorMessage && (
                      <div className="mb-3 px-3 py-2 rounded-lg text-sm" style={{ background: "oklch(0.88 0.12 25 / 15%)", border: "1px solid oklch(0.88 0.12 25 / 30%)", color: "oklch(0.25 0.12 25)" }}>
                        <strong>Error:</strong> {log.errorMessage}
                      </div>
                    )}
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "oklch(0.28 0.03 240)" }}>Raw Payload</p>
                    <pre
                      className="text-xs rounded-lg p-3 overflow-x-auto"
                      style={{
                        background: "oklch(0.12 0.04 240)",
                        color: "oklch(0.88 0.005 240)",
                        fontFamily: "'Courier New', monospace",
                        maxHeight: "300px",
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {payload}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs" style={{ color: "oklch(0.28 0.03 240)" }}>
        <span><span className="font-semibold" style={{ color: "oklch(0.28 0.12 145)" }}>processed</span> — accepted and stored in DB</span>
        <span><span className="font-semibold" style={{ color: "oklch(0.35 0.12 25)" }}>error</span> — rejected (missing fields, auth failure)</span>
        <span><span className="font-semibold" style={{ color: "oklch(0.28 0.08 240)" }}>received</span> — hit server but not yet processed</span>
        <span>✓ body — message_body field was non-empty</span>
        <span>⚠ no body — message_body was blank (Zap mapping issue)</span>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  // Handle Xero OAuth callback result from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroAuth = params.get("xero_auth");
    if (xeroAuth === "success") {
      toast.success("Xero connected", {
        description: "APX's Xero account is now linked to the hub.",
      });
      window.history.replaceState({}, "", "/settings");
    } else if (xeroAuth === "denied") {
      toast.error("Xero access was denied.");
      window.history.replaceState({}, "", "/settings");
    } else if (xeroAuth === "error") {
      toast.error("Xero connection failed. Please try again.");
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  // Queries
  const briefingLogsQuery = trpc.briefing.recentLogs.useQuery();

  const triggerBriefing = trpc.briefing.triggerNow.useMutation({
    onSuccess: () => {
      toast.success("Morning briefing triggered", {
        description: "Check your notification bell — the briefing has been sent.",
      });
      briefingLogsQuery.refetch();
    },
    onError: (err) => toast.error("Briefing failed", { description: err.message }),
  });

  const [connectingEmail, setConnectingEmail] = useState<string | null>(null);

  // APX System Account Drive/Gmail connection — uses drive.isAuthorized
  const driveAuthQuery = trpc.drive.isAuthorized.useQuery();
  const googleConfigured = driveAuthQuery.data?.authorized ?? false;

  const APX_SYSTEM_EMAIL = "apxadvisor@gmail.com";
  const APX_SYSTEM_NAME = "APX System";

  const handleConnect = (_memberEmail: string, _memberName: string) => {
    setConnectingEmail(APX_SYSTEM_EMAIL);
    fetch(`/api/trpc/drive.getAuthUrl`)
      .then((r) => r.json())
      .then((data) => {
        const authUrl = data?.result?.data?.json?.url ?? data?.result?.data?.url;
        if (authUrl) {
          window.location.href = authUrl;
        } else {
          toast.error("Could not generate auth URL.", {
            description: "Check that GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET are set in hub secrets.",
          });
          setConnectingEmail(null);
        }
      })
      .catch(() => {
        toast.error("Failed to get auth URL.");
        setConnectingEmail(null);
      });
  };

  const s = {
    page: { background: "oklch(0.94 0.018 220)", minHeight: "100vh", padding: "2rem" },
    card: {
      background: "oklch(0.93 0.008 240)",
      border: "1px solid oklch(0 0 0 / 10%)",
      borderRadius: "1rem",
      padding: "1.5rem",
      marginBottom: "1.5rem",
    },
    sectionTitle: {
      fontFamily: "'Playfair Display', serif",
      color: "oklch(0.08 0.01 240)",
      fontSize: "1rem",
      fontWeight: 700,
      marginBottom: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    label: { color: "oklch(0.14 0.04 240)", fontSize: "0.875rem", textTransform: "uppercase" as const, letterSpacing: "0.05em" },
    value: { color: "oklch(0.12 0.04 240)", fontSize: "0.875rem" },
    btn: (variant: "gold" | "outline" | "danger") => ({
      padding: "0.5rem 1rem",
      borderRadius: "0.5rem",
      fontSize: "0.9rem",
      fontWeight: 600,
      cursor: "pointer",
      border: "1px solid",
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
      transition: "opacity 0.15s",
      ...(variant === "gold"
        ? { background: "linear-gradient(135deg, oklch(0.82 0.01 240), oklch(0.55 0.01 240))", color: "oklch(0.12 0.04 240)", borderColor: "transparent" }
        : variant === "danger"
        ? { background: "oklch(0.15 0.01 240 / 20%)", color: "oklch(0.08 0.01 240)", borderColor: "oklch(0.15 0.01 240 / 40%)" }
        : { background: "oklch(0.95 0.006 240)", color: "oklch(0.14 0.04 240)", borderColor: "oklch(1 0 0 / 10%)" }),
    }),
    badge: (connected: boolean) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      padding: "0.2rem 0.6rem",
      borderRadius: "9999px",
      fontSize: "0.875rem",
      fontWeight: 600,
      background: connected ? "oklch(0.92 0.005 240 / 15%)" : "oklch(0.5 0.02 240 / 15%)",
      color: connected ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
      border: `1px solid ${connected ? "oklch(0.92 0.005 240 / 30%)" : "oklch(0.5 0.02 240 / 20%)"}`,
    }),
  };

  return (
    <DashboardLayout>
    <div>
      {/* Header — back button removed; DashboardLayout sidebar handles navigation */}

      <h1
        className="text-2xl font-bold mb-1"
        style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)" }}
      >
        {isSuperAdmin(user?.role) ? "Admin Settings" : "Settings"}
      </h1>
      <p className="text-sm mb-8" style={{ color: "oklch(0.14 0.04 240)" }}>
        {isSuperAdmin(user?.role) ? "Manage integrations, automated workflows, and system configuration." : "Manage team access and staff accounts."}
      </p>

      {/* ─── Admin-only sections (hidden from controller) ─────────────────── */}
      {isSuperAdmin(user?.role) && (<>

      {/* ─── APX System Account (Drive + Gmail) ─────────────────────────── */}
      <div style={s.card}>
        <div style={s.sectionTitle}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
          </svg>
          APX System Account — Drive &amp; Gmail
        </div>
        <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
          Authorize <strong style={{ color: "oklch(0.12 0.04 240)" }}>apxadvisor@gmail.com</strong> once to activate
          Google Drive access for all team members. This is the shared APX account used for Drive document
          management and Gmail. Re-authorize whenever scopes change (e.g., adding Drive write access).
        </p>

        {!googleConfigured && (
          <div
            className="rounded-lg p-3 mb-4 text-sm"
            style={{ background: "oklch(0.92 0.005 240 / 10%)", border: "1px solid oklch(0.92 0.005 240 / 25%)", color: "oklch(0.08 0.01 240)" }}
          >
            <strong>Setup required:</strong> Add <code>GOOGLE_CLIENT_ID</code>, <code>GOOGLE_CLIENT_SECRET</code>, and{" "}
            <code>GOOGLE_REDIRECT_URI</code> to the hub secrets first.
          </div>
        )}

        {(() => {
          const isConnected = googleConfigured;
          return (
            <div
              className="flex items-center justify-between rounded-lg px-4 py-3"
              style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(1 0 0 / 6%)" }}
            >
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span style={{ color: "oklch(0.12 0.04 240)", fontWeight: 600, fontSize: "0.875rem" }}>
                    APX System Account
                  </span>
                  <span style={s.badge(isConnected)}>
                    {isConnected ? <><CheckIcon /> Connected — Drive &amp; Gmail Active</> : "Not connected"}
                  </span>
                </div>
                <span style={{ color: "oklch(0.28 0.03 240)", fontSize: "0.875rem" }}>{APX_SYSTEM_EMAIL}</span>
                {isConnected && (
                  <div style={{ color: "oklch(0.08 0.01 240)", fontSize: "0.875rem", marginTop: "0.15rem" }}>
                    ✓ Drive read/write active &nbsp;·&nbsp; ✓ Gmail integration active
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  style={s.btn("gold")}
                  onClick={() => handleConnect(APX_SYSTEM_EMAIL, APX_SYSTEM_NAME)}
                  disabled={connectingEmail === APX_SYSTEM_EMAIL}
                >
                  <LinkIcon />
                  {connectingEmail === APX_SYSTEM_EMAIL ? "Redirecting…" : isConnected ? "Re-authorize" : "Connect Google"}
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      <FaviconSettings />

      {/* ─── Morning Briefing ─────────────────────────────────────────────── */}
      <div style={s.card}>
        <div style={s.sectionTitle}>
          <BriefingIcon />
          Morning Briefing — Workflow 1.3
        </div>
        <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
          Every weekday at <strong style={{ color: "oklch(0.08 0.01 240)" }}>7:00 AM ET</strong>, the hub queries
          the database, generates an AI-written summary of the team's close progress, and delivers it to your
          notification bell. You can also trigger it manually below.
        </p>

        <div className="flex items-center gap-4 mb-5">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: "oklch(0.92 0.005 240 / 10%)", border: "1px solid oklch(0.92 0.005 240 / 25%)", color: "oklch(0.08 0.01 240)" }}
          >
            <span className="w-2 h-2 rounded-full bg-[oklch(0.7_0.14_145)] animate-pulse inline-block" />
            Active — runs weekdays 7:00 AM ET
          </div>
          <button
            style={s.btn("gold")}
            onClick={() => triggerBriefing.mutate()}
            disabled={triggerBriefing.isPending}
          >
            {triggerBriefing.isPending ? "Running…" : "Run Now"}
          </button>
        </div>

        {/* Recent briefing log */}
        {briefingLogsQuery.data && briefingLogsQuery.data.length > 0 && (
          <div>
            <p style={{ ...s.label, marginBottom: "0.5rem" }}>Recent Briefings</p>
            <div className="space-y-2">
              {[...briefingLogsQuery.data].reverse().map((log) => (
                <div
                  key={log.id}
                  className="rounded-lg px-3 py-2.5"
                  style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(1 0 0 / 6%)" }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ color: "oklch(0.08 0.01 240)", fontSize: "0.875rem", fontWeight: 600 }}>
                      {log.briefingDate}
                    </span>
                    <div className="flex gap-3 text-sm" style={{ color: "oklch(0.14 0.04 240)" }}>
                      <span style={{ color: "oklch(0.08 0.01 240)" }}>{log.clientsComplete} complete</span>
                      <span style={{ color: "oklch(0.08 0.01 240)" }}>{log.clientsInProgress} in progress</span>
                      {log.clientsOverdue > 0 && (
                        <span style={{ color: "oklch(0.08 0.01 240)" }}>{log.clientsOverdue} overdue</span>
                      )}
                    </div>
                  </div>
                  <p style={{ color: "oklch(0.14 0.04 240)", fontSize: "0.875rem", lineHeight: 1.5 }}>
                    {log.summary.slice(0, 180)}{log.summary.length > 180 ? "…" : ""}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {briefingLogsQuery.data?.length === 0 && (
          <p style={{ color: "oklch(0.25 0.03 240)", fontSize: "0.9rem" }}>
            No briefings sent yet. Click "Run Now" to generate the first one.
          </p>
        )}
      </div>

      {/* ─── Workflow Status Summary ──────────────────────────────────────── */}
      <div style={s.card}>
        <div style={s.sectionTitle}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
          </svg>
          Automated Workflows
        </div>
        <div className="space-y-2">
          {[
            { id: "1.3", name: "Morning Briefing", desc: "Live news headlines (business, economy, tax) + AI summary → Admin notification bell (weekdays 7 AM ET)", status: "active" },

            { id: "2.x", name: "Client Comms (Phase 2)", desc: "Missing statement SMS, post-meeting follow-up, onboarding sequence", status: "planned" },
            { id: "3.x", name: "Xero Bookkeeping AI (Phase 3)", desc: "AI transaction categorisation, P&L narrative, reconciliation check", status: "planned" },
          ].map((wf) => (
            <div
              key={wf.id}
              className="flex items-center justify-between rounded-lg px-3 py-2.5"
              style={{ background: "oklch(0.94 0.018 220)", border: "1px solid oklch(1 0 0 / 6%)" }}
            >
              <div>
                <div className="flex items-center gap-2">
                  <span style={{ color: "oklch(0.08 0.01 240)", fontSize: "0.875rem", fontWeight: 700 }}>
                    WF {wf.id}
                  </span>
                  <span style={{ color: "oklch(0.12 0.04 240)", fontSize: "0.9rem", fontWeight: 600 }}>
                    {wf.name}
                  </span>
                </div>
                <p style={{ color: "oklch(0.14 0.04 240)", fontSize: "0.875rem", marginTop: "0.1rem" }}>{wf.desc}</p>
              </div>
              <span
                style={{
                  padding: "0.2rem 0.6rem",
                  borderRadius: "9999px",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  background: wf.status === "active" ? "oklch(0.92 0.005 240 / 15%)" : wf.status === "pending" ? "oklch(0.92 0.005 240 / 15%)" : "oklch(0.5 0.02 240 / 10%)",
                  color: wf.status === "active" ? "oklch(0.08 0.01 240)" : wf.status === "pending" ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)",
                  border: `1px solid ${wf.status === "active" ? "oklch(0.92 0.005 240 / 30%)" : wf.status === "pending" ? "oklch(0.92 0.005 240 / 30%)" : "oklch(0.5 0.02 240 / 15%)"}`,
                  whiteSpace: "nowrap" as const,
                }}
              >
                {wf.status}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* ── End of admin-only sections ── */}
      </>)}

      {/* ── Xero Integration — visible to admin AND controller ── */}
      {isManager(user?.role) && (
        <XeroCard />
      )}

      {/* Client Hub removed — no longer integrated with APX Hub */}

      {/* ── Standard Connection Protocol — placeholder to preserve structure ── */}
      {false && (
        <div style={s.card}>
          <div style={s.sectionTitle}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
            </svg>
            Removed
            <span
              style={{
                marginLeft: "auto",
                fontSize: "0.85rem",
                fontWeight: 700,
                padding: "0.2rem 0.6rem",
                borderRadius: "9999px",
                background: "oklch(0.92 0.005 240 / 15%)",
                color: "oklch(0.08 0.01 240)",
                border: "1px solid oklch(0.92 0.005 240 / 30%)",
                letterSpacing: "0.05em",
                textTransform: "uppercase" as const,
              }}
            >
              🔒 DIRECTOR PROTECTED
            </span>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-3 mb-4">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ background: "oklch(0.55 0.01 240)", boxShadow: "0 0 8px oklch(0.92 0.005 240 / 60%)" }}
            />
            <span style={{ color: "oklch(0.12 0.04 240)", fontSize: "0.875rem", fontWeight: 600 }}>
              Active — 2 shared seats
            </span>
            <span
              style={{
                fontSize: "0.875rem",
                padding: "0.2rem 0.6rem",
                borderRadius: "9999px",
                background: "oklch(0.92 0.005 240 / 12%)",
                color: "oklch(0.08 0.01 240)",
                border: "1px solid oklch(0.92 0.005 240 / 25%)",
              }}
            >
              Connected
            </span>
          </div>

          <p className="text-sm mb-5" style={{ color: "oklch(0.14 0.04 240)" }}>
            Client Hub is the firm's primary CRM and client management platform. All 123 client records are linked
            via their Client Hub ID. This connection is <strong style={{ color: "oklch(0.08 0.01 240)" }}>protected</strong> —
            any modification requires express Director approval.
          </p>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            <a
              href="https://app.clienthub.com"
              target="_blank"
              rel="noopener noreferrer"
              style={s.btn("gold")}
              className="inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
              </svg>
              Open Client Hub
            </a>
            <a
              href="https://app.clienthub.com/settings"
              target="_blank"
              rel="noopener noreferrer"
              style={s.btn("outline")}
              className="inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Client Hub Settings
            </a>
          </div>

          {/* Protection notice */}
          <div
            className="mt-4 rounded-lg px-4 py-3 text-sm"
            style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "1px solid oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)" }}
          >
            <strong style={{ color: "oklch(0.08 0.01 240)" }}>Protected Integration:</strong> The Client Hub connection and all 123 client Hub IDs
            are locked. Any changes to this integration require express approval from Mr. B (Director).
            To modify, contact the system administrator directly.
          </div>
        </div>
      )}

      {/* ── Standard Connection Protocol — admin only ── */}
      {isSuperAdmin(user?.role) && <ConnectionSettingsCard />}

      {/* ── Webhook Diagnostics — admin only ── */}
      {isSuperAdmin(user?.role) && <WebhookDiagnosticsCard />}

      {/* ── Team & Access — visible to admin AND controller ── */}
      <div style={s.card}>
        {/* ─── Add New Client ─────────────────────────────────────────────── */}
        <AddNewClientSection />

        <div style={s.sectionTitle}>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 10-6 0" />
          </svg>
          Team &amp; Access
        </div>
        <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
          Manage staff accounts, assign roles, reset passwords, and add new team members.
        </p>
        <button
          onClick={() => navigate("/settings/staff")}
          style={s.btn("gold")}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5-3.87M9 20H4v-2a4 4 0 015-3.87m6-4a4 4 0 11-8 0 4 4 0 018 0zm6 4a3 3 0 10-6 0" />
          </svg>
          Open Team &amp; Access
        </button>
      </div>

      {/* ─── Contact Data Import — admin only ─────────────────────────────────── */}
      {isSuperAdmin(user?.role) && <ContactImportCard />}

    </div>
    </DashboardLayout>
  );
}

// ─── Add New Client Section ───────────────────────────────────────────────────
function AddNewClientSection() {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [practiceKey, setPracticeKey] = useState<"apx" | "apx-hrb" | "apx-bbn">("apx-hrb");
  const [xeroTenantId, setXeroTenantId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [accountingPlatform, setAccountingPlatform] = useState<"xero" | "quickbooks" | "other">("xero");
  const [contactPerson, setContactPerson] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientState, setClientState] = useState("CA");
  const [industry, setIndustry] = useState("");

  const addClient = trpc.xeroPm.addCoreClient.useMutation({
    onSuccess: (data) => {
      toast.success(`✅ "${data.client?.name}" added successfully to ${practiceKey.toUpperCase()}`);
      setName("");
      setXeroTenantId("");
      setContactPerson("");
      setClientEmail("");
      setClientPhone("");
      setClientState("CA");
      setIndustry("");
      setAccountingPlatform("xero");
      setIsOpen(false);
    },
    onError: (err) => {
      toast.error(`Failed to add client: ${err.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Client name is required"); return; }
    if (!user?.email) { toast.error("Unable to confirm your account. Please sign in again."); return; }
    addClient.mutate({
      callerEmail: user.email,
      name: name.trim().toUpperCase(),
      coreName: name.trim().toUpperCase(),
      practiceKey,
      xeroTenantId: xeroTenantId.trim() || null,
      isActive: true,
      accountingPlatform,
      contactPerson: contactPerson.trim() || undefined,
      clientEmail: clientEmail.trim() || undefined,
      clientPhone: clientPhone.trim() || undefined,
      clientState: clientState.trim() || undefined,
      industry: industry.trim() || undefined,
    });
  };

  const s = {
    sectionTitle: {
      display: "flex", alignItems: "center", gap: "8px",
      fontSize: "13px", fontWeight: 700, letterSpacing: "0.06em",
      color: "oklch(0.12 0.04 240)", textTransform: "uppercase" as const,
      marginBottom: "12px",
    },
    input: {
      width: "100%", padding: "9px 12px", borderRadius: "6px",
      border: "1px solid oklch(0.82 0.02 240)", fontSize: "13px",
      background: "oklch(0.98 0.005 240)", color: "oklch(0.12 0.04 240)",
      outline: "none",
    },
    select: {
      width: "100%", padding: "9px 12px", borderRadius: "6px",
      border: "1px solid oklch(0.82 0.02 240)", fontSize: "13px",
      background: "oklch(0.98 0.005 240)", color: "oklch(0.12 0.04 240)",
      outline: "none", cursor: "pointer",
    },
    label: {
      display: "block", fontSize: "11px", fontWeight: 600,
      letterSpacing: "0.05em", textTransform: "uppercase" as const,
      color: "oklch(0.35 0.04 240)", marginBottom: "4px",
    },
    btn: {
      display: "flex", alignItems: "center", gap: "6px",
      padding: "9px 18px", borderRadius: "6px", border: "none",
      fontSize: "13px", fontWeight: 600, cursor: "pointer",
      background: "oklch(0.72 0.14 75)", color: "oklch(0.12 0.04 240)",
    },
    cancelBtn: {
      display: "flex", alignItems: "center", gap: "6px",
      padding: "9px 18px", borderRadius: "6px",
      border: "1px solid oklch(0.82 0.02 240)",
      fontSize: "13px", fontWeight: 600, cursor: "pointer",
      background: "transparent", color: "oklch(0.35 0.04 240)",
    },
  };

  return (
    <div style={{ marginBottom: "28px" }}>
      <div style={s.sectionTitle}>
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        Add New Client
      </div>
      <p className="text-sm mb-4" style={{ color: "oklch(0.14 0.04 240)" }}>
        Manually add a new company to the APX Hub client list. Once added, you can link their Xero account when available.
      </p>

      {!isOpen ? (
        <button style={s.btn} onClick={() => setIsOpen(true)}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add New Client
        </button>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "480px" }}>
          <div>
            <label style={s.label}>Company Name *</label>
            <input
              style={s.input}
              type="text"
              placeholder="e.g. DUTCH CLEANING & MAINTENANCE SERVICES INC"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label style={s.label}>Practice Entity *</label>
            <select
              style={s.select}
              value={practiceKey}
              onChange={e => setPracticeKey(e.target.value as "apx" | "apx-hrb" | "apx-bbn")}
            >
              <option value="apx">APX Advisors LLC</option>
              <option value="apx-hrb">APX-HRB</option>
              <option value="apx-bbn">APX-BBN</option>
            </select>
          </div>

          <div>
            <label style={s.label}>Accounting Platform *</label>
            <select
              style={s.select}
              value={accountingPlatform}
              onChange={e => {
                setAccountingPlatform(e.target.value as "xero" | "quickbooks" | "other");
                if (e.target.value !== "xero") setXeroTenantId("");
              }}
            >
              <option value="xero">Xero</option>
              <option value="quickbooks">QuickBooks</option>
              <option value="other">Other</option>
            </select>
          </div>

          {accountingPlatform === "xero" && (
            <div>
              <label style={s.label}>Xero Tenant ID (optional)</label>
              <input
                style={s.input}
                type="text"
                placeholder="Leave blank — link later once connected in Xero"
                value={xeroTenantId}
                onChange={e => setXeroTenantId(e.target.value)}
              />
              <p style={{ fontSize: "11px", color: "oklch(0.45 0.04 240)", marginTop: "4px" }}>
                You can leave this blank and link the Xero account later from the Xero Reports page.
              </p>
            </div>
          )}
          {accountingPlatform !== "xero" && (
            <p style={{ fontSize: "12px", color: "oklch(0.40 0.06 75)", padding: "8px 12px", background: "oklch(0.97 0.02 75)", borderRadius: "6px", border: "1px solid oklch(0.88 0.06 75)" }}>
              This client will be tracked as a <strong>{accountingPlatform === "quickbooks" ? "QuickBooks" : "Other"}</strong> client. They will appear in the Schedule &amp; Task Assignment matrix and can be assigned bookkeepers and due dates like any Xero client.
            </p>
          )}

          {/* ─── Contact Information (optional) ─── */}
          <div style={{ borderTop: "1px solid oklch(0.88 0.02 240)", paddingTop: "14px", marginTop: "4px" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" as const, color: "oklch(0.45 0.04 240)", marginBottom: "10px" }}>Contact Information (Optional)</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <div>
                <label style={s.label}>Contact Person</label>
                <input style={s.input} type="text" placeholder="e.g. John Smith" value={contactPerson} onChange={e => setContactPerson(e.target.value)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label style={s.label}>Email</label>
                  <input style={s.input} type="email" placeholder="client@example.com" value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
                </div>
                <div>
                  <label style={s.label}>Phone</label>
                  <input style={s.input} type="tel" placeholder="(310) 555-1234" value={clientPhone} onChange={e => setClientPhone(e.target.value)} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label style={s.label}>State</label>
                  <input style={s.input} type="text" placeholder="CA" maxLength={2} value={clientState} onChange={e => setClientState(e.target.value.toUpperCase())} />
                </div>
                <div>
                  <label style={s.label}>Industry</label>
                  <input style={s.input} type="text" placeholder="e.g. Restaurant, Real Estate" value={industry} onChange={e => setIndustry(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button type="submit" style={s.btn} disabled={addClient.isPending}>
              {addClient.isPending ? "Adding…" : "Add Client"}
            </button>
            <button type="button" style={s.cancelBtn} onClick={() => setIsOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Contact Data Import Card ─────────────────────────────────────────────────
function ContactImportCard() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [columns, setColumns] = useState<string[]>([]);
  const [colMap, setColMap] = useState<{ name: string; email: string; phone: string }>({ name: "", email: "", phone: "" });
  const [preview, setPreview] = useState<Array<{ clientName: string; email: string; phone: string; _valid: boolean }>>([])
  const [result, setResult] = useState<{ total: number; matched: number; updated: number; skipped: number; errors: string[] } | null>(null);
  const [parseError, setParseError] = useState("");
  const [step, setStep] = useState<"upload" | "map" | "preview" | "done">("upload");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();

  // ── Coverage stats ─────────────────────────────────────────────────────────
  const coverageQuery = trpc.clientStatus.getContactCoverage.useQuery(undefined, { staleTime: 30_000 });
  const coverage = coverageQuery.data;

  // ── Import mutation ────────────────────────────────────────────────────────
  const importMutation = trpc.clientStatus.importContactData.useMutation({
    onSuccess: (data) => {
      setResult(data);
      setStep("done");
      toast.success(`Import complete — ${data.updated} clients updated`);
      utils.clientStatus.getContactCoverage.invalidate();
    },
    onError: (err) => toast.error("Import failed", { description: err.message }),
  });

  // ── CSV parsing helper ─────────────────────────────────────────────────────
  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ""; }
      else { current += ch; }
    }
    result.push(current.trim());
    return result;
  };

  // ── Load CSV text → detect columns ────────────────────────────────────────
  const loadCsv = useCallback((text: string, name?: string) => {
    setCsvText(text);
    setFileName(name ?? "");
    setPreview([]);
    setResult(null);
    setParseError("");
    setStep("map");
    const lines = text.trim().split("\n").filter(l => l.trim());
    if (lines.length === 0) { setParseError("Empty file."); setStep("upload"); return; }
    const firstLine = lines[0];
    const cols = parseCsvLine(firstLine);
    setColumns(cols);
    // Auto-detect common column names
    const autoMap = { name: "", email: "", phone: "" };
    cols.forEach(c => {
      const lc = c.toLowerCase();
      if (!autoMap.name && (lc.includes("client") || lc.includes("name") || lc === "company")) autoMap.name = c;
      if (!autoMap.email && (lc.includes("email") || lc.includes("mail"))) autoMap.email = c;
      if (!autoMap.phone && (lc.includes("phone") || lc.includes("mobile") || lc.includes("tel"))) autoMap.phone = c;
    });
    setColMap(autoMap);
  }, []);

  // ── File input / drag-drop ─────────────────────────────────────────────────
  const handleFile = (file: File) => {
    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      toast.error("Please upload a .csv file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => loadCsv(e.target?.result as string, file.name);
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // ── Build preview from mapped columns ─────────────────────────────────────
  const handleBuildPreview = () => {
    if (!colMap.name) { setParseError("Please map the Client Name column."); return; }
    setParseError("");
    const lines = csvText.trim().split("\n").filter(l => l.trim());
    const headerLine = lines[0];
    const headers = parseCsvLine(headerLine);
    const nameIdx = headers.indexOf(colMap.name);
    const emailIdx = colMap.email ? headers.indexOf(colMap.email) : -1;
    const phoneIdx = colMap.phone ? headers.indexOf(colMap.phone) : -1;
    const dataLines = lines.slice(1);
    const rows: Array<{ clientName: string; email: string; phone: string; _valid: boolean }> = [];
    for (const line of dataLines) {
      if (!line.trim()) continue;
      const parts = parseCsvLine(line);
      const clientName = (parts[nameIdx] ?? "").replace(/^"|"$/g, "").trim();
      const email = emailIdx >= 0 ? (parts[emailIdx] ?? "").replace(/^"|"$/g, "").trim() : "";
      const phone = phoneIdx >= 0 ? (parts[phoneIdx] ?? "").replace(/^"|"$/g, "").trim() : "";
      if (!clientName) continue;
      const _valid = !!(email || phone);
      rows.push({ clientName, email, phone, _valid });
    }
    if (rows.length === 0) { setParseError("No data rows found after the header."); return; }
    setPreview(rows);
    setStep("preview");
  };

  const handleImport = () => {
    if (preview.length === 0) return;
    importMutation.mutate({
      rows: preview.map(r => ({ clientName: r.clientName, email: r.email || undefined, phone: r.phone || undefined })),
    });
  };

  const handleReset = () => {
    setCsvText(""); setFileName(""); setColumns([]); setColMap({ name: "", email: "", phone: "" });
    setPreview([]); setResult(null); setParseError(""); setStep("upload");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    const header = "CLIENT_NAME,EMAIL,PHONE\n";
    const example = "1990 PLUMBING INC,contact@example.com,+13105550100\nA&C CORP,owner@acorp.com,+13235550199\n";
    const blob = new Blob([header + example], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "APX_Contact_Import_Template.csv";
    a.click();
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const s = {
    card: { background: "oklch(0.93 0.008 240)", border: "1px solid oklch(0 0 0 / 10%)", borderRadius: "1rem", padding: "1.5rem", marginBottom: "1.5rem" } as React.CSSProperties,
    title: { fontFamily: "'Playfair Display', serif", color: "oklch(0.08 0.01 240)", fontSize: "1rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" } as React.CSSProperties,
    label: { fontSize: "0.75rem", fontWeight: 700, color: "oklch(0.28 0.04 240)", textTransform: "uppercase" as const, letterSpacing: "0.06em", display: "block", marginBottom: "4px" } as React.CSSProperties,
    btn: (variant: "gold" | "green" | "outline") => ({
      display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.45rem 1rem",
      borderRadius: "0.5rem", fontSize: "0.875rem", fontWeight: 600, cursor: "pointer",
      border: variant === "outline" ? "1px solid oklch(0 0 0 / 20%)" : "none",
      background: variant === "gold" ? "oklch(0.72 0.14 75)" : variant === "green" ? "oklch(0.50 0.18 145)" : "transparent",
      color: variant === "outline" ? "oklch(0.14 0.04 240)" : variant === "green" ? "white" : "oklch(0.08 0.01 240)",
      opacity: 1,
    } as React.CSSProperties),
    select: { padding: "0.35rem 0.6rem", borderRadius: "0.4rem", border: "1px solid oklch(0 0 0 / 15%)", background: "oklch(0.96 0.005 240)", color: "oklch(0.08 0.01 240)", fontSize: "0.85rem", cursor: "pointer" } as React.CSSProperties,
  };

  const validRows = preview.filter(r => r._valid).length;
  const invalidRows = preview.length - validRows;

  return (
    <div style={s.card}>
      {/* ── Header ── */}
      <div style={s.title}>
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
        Bulk Import Client Contact Data
      </div>

      {/* ── Coverage summary bar ── */}
      {coverage && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", marginBottom: "1.25rem" }}>
          {[
            { label: "Total Clients", val: coverage.total, color: "oklch(0.08 0.01 240)" },
            { label: "Has Email", val: coverage.hasEmail, color: "oklch(0.30 0.15 145)" },
            { label: "Has Phone", val: coverage.hasPhone, color: "oklch(0.30 0.12 240)" },
            { label: "Missing Both", val: coverage.missingBoth, color: "oklch(0.45 0.20 25)" },
          ].map(({ label, val, color }) => (
            <div key={label} style={{ background: "oklch(0.96 0.005 240)", borderRadius: "0.5rem", padding: "0.6rem 0.75rem", textAlign: "center", border: "1px solid oklch(0 0 0 / 8%)" }}>
              <div style={{ fontSize: "1.4rem", fontWeight: 700, color }}>{val}</div>
              <div style={{ fontSize: "0.7rem", color: "oklch(0.35 0.02 240)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Step 1: Upload ── */}
      {step === "upload" && (
        <>
          <p style={{ fontSize: "0.875rem", color: "oklch(0.14 0.04 240)", marginBottom: "1rem" }}>
            Upload a CSV file with client names, emails, and/or phone numbers. Rows are matched to the Hub by client name (case-insensitive). Only non-blank fields are written — existing data is not overwritten by blank values.
          </p>
          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragging ? "oklch(0.72 0.14 75)" : "oklch(0 0 0 / 20%)"}`,
              borderRadius: "0.75rem",
              padding: "2rem",
              textAlign: "center",
              cursor: "pointer",
              background: isDragging ? "oklch(0.72 0.14 75 / 8%)" : "oklch(0.96 0.005 240)",
              transition: "all 0.15s",
              marginBottom: "1rem",
            }}
          >
            <svg className="w-8 h-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: "oklch(0.45 0.03 240)" }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <p style={{ fontWeight: 600, color: "oklch(0.14 0.04 240)", fontSize: "0.9rem", marginBottom: "0.25rem" }}>Drop CSV file here or click to browse</p>
            <p style={{ fontSize: "0.8rem", color: "oklch(0.45 0.03 240)" }}>Accepts .csv files — any column order, with or without header row</p>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button style={s.btn("outline")} onClick={handleDownloadTemplate}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
              Download Template
            </button>
            <span style={{ fontSize: "0.8rem", color: "oklch(0.45 0.03 240)" }}>or paste CSV text below</span>
          </div>
          {/* Paste fallback */}
          <div style={{ marginTop: "0.75rem" }}>
            <label style={s.label}>Paste CSV text directly</label>
            <textarea
              style={{ width: "100%", minHeight: "100px", fontFamily: "monospace", fontSize: "0.8rem", padding: "0.6rem", borderRadius: "0.5rem", border: "1px solid oklch(0 0 0 / 15%)", background: "oklch(0.96 0.005 240)", color: "oklch(0.08 0.01 240)", resize: "vertical" }}
              placeholder={"CLIENT_NAME,EMAIL,PHONE\n1990 PLUMBING INC,contact@example.com,+13105550100"}
              onChange={e => { if (e.target.value.trim()) loadCsv(e.target.value); }}
            />
          </div>
          {parseError && <p style={{ color: "oklch(0.45 0.2 25)", fontSize: "0.8rem", marginTop: "4px" }}>{parseError}</p>}
        </>
      )}

      {/* ── Step 2: Column Mapping ── */}
      {step === "map" && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <div style={{ background: "oklch(0.72 0.14 75 / 15%)", borderRadius: "0.5rem", padding: "0.4rem 0.75rem", fontSize: "0.8rem", fontWeight: 600, color: "oklch(0.35 0.10 75)" }}>
              📄 {fileName || "Pasted CSV"} — {columns.length} columns detected
            </div>
            <button style={{ ...s.btn("outline"), fontSize: "0.8rem", padding: "0.3rem 0.6rem" }} onClick={handleReset}>← Back</button>
          </div>
          <p style={{ fontSize: "0.875rem", color: "oklch(0.14 0.04 240)", marginBottom: "1rem" }}>
            Map your CSV columns to the Hub fields. <strong>Client Name</strong> is required for matching. Email and Phone are optional but at least one is needed to update a record.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
            {([
              { key: "name" as const, label: "Client Name *", required: true },
              { key: "email" as const, label: "Email", required: false },
              { key: "phone" as const, label: "Phone", required: false },
            ]).map(({ key, label, required }) => (
              <div key={key}>
                <label style={s.label}>{label}</label>
                <select
                  style={{ ...s.select, width: "100%", borderColor: required && !colMap[key] ? "oklch(0.55 0.20 25)" : undefined }}
                  value={colMap[key]}
                  onChange={e => setColMap(prev => ({ ...prev, [key]: e.target.value }))}
                >
                  <option value="">— not mapped —</option>
                  {columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          {parseError && <p style={{ color: "oklch(0.45 0.2 25)", fontSize: "0.8rem", marginBottom: "0.75rem" }}>{parseError}</p>}
          <button style={s.btn("gold")} onClick={handleBuildPreview} disabled={!colMap.name}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            Preview Import
          </button>
        </>
      )}

      {/* ── Step 3: Preview ── */}
      {step === "preview" && preview.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <span style={{ background: "oklch(0.50 0.18 145 / 15%)", color: "oklch(0.25 0.12 145)", borderRadius: "0.4rem", padding: "0.3rem 0.6rem", fontSize: "0.8rem", fontWeight: 600 }}>
                ✓ {validRows} rows with data
              </span>
              {invalidRows > 0 && (
                <span style={{ background: "oklch(0.55 0.20 25 / 12%)", color: "oklch(0.40 0.18 25)", borderRadius: "0.4rem", padding: "0.3rem 0.6rem", fontSize: "0.8rem", fontWeight: 600 }}>
                  ⚠ {invalidRows} rows with no email/phone (will be skipped)
                </span>
              )}
            </div>
            <button style={{ ...s.btn("outline"), fontSize: "0.8rem", padding: "0.3rem 0.6rem", marginLeft: "auto" }} onClick={() => setStep("map")}>← Remap</button>
          </div>
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "340px", borderRadius: "0.5rem", border: "1px solid oklch(0 0 0 / 10%)", marginBottom: "1rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr style={{ background: "oklch(0.90 0.01 240)" }}>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "oklch(0.14 0.04 240)", fontWeight: 700, whiteSpace: "nowrap" }}>#</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "oklch(0.14 0.04 240)", fontWeight: 700, whiteSpace: "nowrap" }}>CLIENT NAME</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "oklch(0.14 0.04 240)", fontWeight: 700, whiteSpace: "nowrap" }}>EMAIL</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "oklch(0.14 0.04 240)", fontWeight: 700, whiteSpace: "nowrap" }}>PHONE</th>
                  <th style={{ padding: "6px 10px", textAlign: "left", color: "oklch(0.14 0.04 240)", fontWeight: 700, whiteSpace: "nowrap" }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} style={{ borderTop: "1px solid oklch(0 0 0 / 6%)", background: row._valid ? undefined : "oklch(0.55 0.20 25 / 5%)" }}>
                    <td style={{ padding: "5px 10px", color: "oklch(0.45 0.02 240)" }}>{i + 1}</td>
                    <td style={{ padding: "5px 10px", color: "oklch(0.08 0.01 240)", fontWeight: 600, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.clientName}</td>
                    <td style={{ padding: "5px 10px", color: row.email ? "oklch(0.14 0.04 240)" : "oklch(0.55 0.02 240)" }}>{row.email || "—"}</td>
                    <td style={{ padding: "5px 10px", color: row.phone ? "oklch(0.14 0.04 240)" : "oklch(0.55 0.02 240)" }}>{row.phone || "—"}</td>
                    <td style={{ padding: "5px 10px" }}>
                      {row._valid
                        ? <span style={{ color: "oklch(0.30 0.15 145)", fontWeight: 600, fontSize: "0.75rem" }}>✓ Ready</span>
                        : <span style={{ color: "oklch(0.45 0.18 25)", fontSize: "0.75rem" }}>Skip (no data)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              style={{ ...s.btn("green"), opacity: importMutation.isPending ? 0.7 : 1 }}
              onClick={handleImport}
              disabled={importMutation.isPending || validRows === 0}
            >
              {importMutation.isPending ? (
                <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={3} strokeDasharray="30 60" /></svg> Importing…</>
              ) : (
                <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg> Import {validRows} Rows</>
              )}
            </button>
            <button style={s.btn("outline")} onClick={handleReset}>Cancel</button>
          </div>
        </>
      )}

      {/* ── Step 4: Done ── */}
      {step === "done" && result && (
        <>
          <div style={{ borderRadius: "0.5rem", padding: "1.25rem", background: "oklch(0.50 0.18 145 / 10%)", border: "1px solid oklch(0.50 0.18 145 / 30%)", marginBottom: "1rem" }}>
            <p style={{ fontWeight: 700, color: "oklch(0.20 0.12 145)", marginBottom: "0.75rem", fontSize: "1rem" }}>✓ Import Complete</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "0.5rem", marginBottom: result.errors.length > 0 ? "0.75rem" : 0 }}>
              {[
                { label: "Total Rows", val: result.total },
                { label: "Matched", val: result.matched },
                { label: "Updated", val: result.updated },
                { label: "Skipped", val: result.skipped },
              ].map(({ label, val }) => (
                <div key={label} style={{ background: "white", borderRadius: "0.4rem", padding: "0.5rem 0.75rem", textAlign: "center" }}>
                  <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "oklch(0.08 0.01 240)" }}>{val}</div>
                  <div style={{ fontSize: "0.75rem", color: "oklch(0.35 0.02 240)" }}>{label}</div>
                </div>
              ))}
            </div>
            {result.errors.length > 0 && (
              <div style={{ fontSize: "0.8rem", color: "oklch(0.45 0.2 25)", marginTop: "0.5rem" }}>
                <strong>Unmatched ({result.errors.length}):</strong> {result.errors.slice(0, 8).join(" · ")}{result.errors.length > 8 ? ` …+${result.errors.length - 8} more` : ""}
              </div>
            )}
          </div>
          <button style={s.btn("outline")} onClick={handleReset}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Import Another File
          </button>
        </>
      )}
    </div>
  );
}
